import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  FaWhatsapp,
  FaMapMarkerAlt,
  FaPalette,
  FaSearch,
  FaRocket,
  FaCheckCircle,
  FaMobileAlt,
  FaStore,
  FaArrowRight,
  FaStar,
  FaPhone,
  FaLayerGroup,
} from "react-icons/fa";
import "./WebsiteDesignerVirarWest.css";

/* ────────────────────────────────────────────────────────────
   WEBSITE DESIGNER IN VIRAR WEST — dedicated SEO landing page
   Primary keyword: "website designer in virar west"
   This page is intentionally standalone (not part of the
   locationPages map) with unique copy so it does not clash /
   duplicate with the existing web-developer-virar-west page.
   ──────────────────────────────────────────────────────────── */

const AREA = "Virar West";
const PRIMARY_KEYWORD = "website designer in Virar West";
const SLUG = "website-designer-virar-west";
const PHONE_DISPLAY = "+91 90040 68462";
const PHONE_TEL = "+919004068462";
const NEARBY = ["Virar", "Nallasopara", "Vasai Road", "Vasai"];

const description =
  "Need a website designer in Virar West? Amresh Prajapati creates fast, mobile-friendly, SEO-ready websites for shops, clinics, coaching classes & startups in Virar West. 4+ years experience, affordable pricing & direct WhatsApp support. Get a free quote today!";

const keywords = [
  PRIMARY_KEYWORD,
  "website design Virar West",
  "web designer near me Virar West",
  "freelance website designer Virar West",
  "affordable website designer Virar West",
  "best website designer Virar West",
  "business website design Virar West",
  "React developer Virar West",
  "ecommerce website designer Virar West",
  "logo and website design Virar West",
  ...NEARBY.map((n) => `website designer ${n}`),
].join(", ");

/* ─── Animated counter hook ─────────────────────────────────── */
function useCounter(end, duration = 1600, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, start]);
  return count;
}

/* ─── Stats section with intersection observer ──────────────── */
function StatsSection() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const c1 = useCounter(50, 1400, visible);
  const c2 = useCounter(98, 1600, visible);
  const c3 = useCounter(5, 1200, visible);
  const c4 = useCounter(100, 1500, visible);

  return (
    <div className="wdv-stats" ref={ref}>
      {[
        { val: c1, suffix: "+", label: "Websites Designed" },
        { val: c2, suffix: "%", label: "Client Satisfaction" },
        { val: c3, suffix: "+", label: "Years Experience" },
        { val: c4, suffix: "%", label: "Mobile Responsive" },
      ].map(({ val, suffix, label }) => (
        <div className="wdv-stat" key={label}>
          <span className="wdv-stat-num">
            {val}
            {suffix}
          </span>
          <span className="wdv-stat-label">{label}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Design services data ───────────────────────────────────── */
const services = [
  {
    icon: <FaPalette />,
    title: "Custom Website Design",
    text: "A unique visual identity for your Virar West business — no recycled templates. Layouts, colors and typography chosen to match your brand.",
  },
  {
    icon: <FaMobileAlt />,
    title: "Mobile-First Design",
    text: "Most of your visitors will open your site on a phone. Every design is built mobile-first, then scaled up to tablet and desktop.",
  },
  {
    icon: <FaLayerGroup />,
    title: "UI/UX & Wireframing",
    text: "Before any code is written, I map out the user journey so every page guides visitors naturally toward calling or messaging you.",
  },
  {
    icon: <FaSearch />,
    title: "Local SEO Foundation",
    text: "Correct meta tags, schema markup and Virar West location keywords built into the design so Google can find and rank your site.",
  },
  {
    icon: <FaRocket />,
    title: "Fast-Loading Pages",
    text: "Optimised images, clean code and smart loading so your website feels instant — a key ranking factor for local search.",
  },
  {
    icon: <FaStore />,
    title: "Business & Ecommerce Design",
    text: "From single-page portfolios to full ecommerce storefronts, the design is matched to how your business actually sells.",
  },
];

/* ─── Process steps ─────────────────────────────────────────── */
const steps = [
  {
    n: "01",
    title: "Understand Your Brand",
    text: "A quick call or WhatsApp chat about your business, audience and the pages you need.",
  },
  {
    n: "02",
    title: "Design the Experience",
    text: "Wireframes, colour palette and a modern responsive design tailored to Virar West customers.",
  },
  {
    n: "03",
    title: "Build, Test & Launch",
    text: "Clean development, mobile testing, SEO setup and a smooth go-live with your approval at every step.",
  },
];

/* ─── FAQ data ───────────────────────────────────────────────── */
const faqs = [
  {
    q: "How much does a website designer in Virar West charge?",
    a: "Website design in Virar West typically starts from ₹8,000 for a basic business site. The final cost depends on the number of pages, features and design complexity — message me for a free custom quote.",
  },
  {
    q: "Do you only design, or do you also develop the website?",
    a: "Both. I handle the full process — design, development, SEO setup and launch — so there's no handoff between a designer and a separate developer.",
  },
  {
    q: "How long does it take to design and launch a website?",
    a: "A standard business website is usually ready in 5–10 working days. Ecommerce stores or custom web apps can take 2–4 weeks depending on scope.",
  },
  {
    q: "Will the design work well on mobile phones?",
    a: "Yes, every design is built mobile-first and tested across phones, tablets and desktops before it goes live.",
  },
  {
    q: "Can you redesign my existing website?",
    a: "Yes. I regularly refresh outdated websites in Virar West with a modern look, faster loading and better mobile experience while keeping your existing content and SEO intact.",
  },
  {
    q: "Do you offer support after the website is live?",
    a: "Yes — direct WhatsApp support for edits, updates and fixes after launch. No middlemen, no ticket queues.",
  },
];

const waLink = (msg) => `https://wa.me/919004068462?text=${encodeURIComponent(msg)}`;

/* ─── Schema.org JSON-LD ─────────────────────────────────────── */
const schemaOrg = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "LocalBusiness",
      "@id": `https://freelanceramreshprajapatimumbai.in/${SLUG}#business`,
      name: "Amresh Prajapati - Website Designer",
      url: `https://freelanceramreshprajapatimumbai.in/${SLUG}`,
      telephone: PHONE_TEL,
      image: "https://freelanceramreshprajapatimumbai.in/og-image.jpg",
      description,
      address: {
        "@type": "PostalAddress",
        addressLocality: AREA,
        addressRegion: "Maharashtra",
        addressCountry: "IN",
      },
      areaServed: [AREA, ...NEARBY, "Mumbai"],
      geo: { "@type": "GeoCoordinates", latitude: 19.4595, longitude: 72.7936 },
      openingHours: "Mo-Su 00:00-23:59",
      priceRange: "₹₹",
      sameAs: [
        "https://www.linkedin.com/in/amreshprajapati",
        "https://github.com/amreshprajapati",
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Website Design Services",
        itemListElement: [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: `Custom Website Design in ${AREA}` } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: `UI/UX & Wireframing in ${AREA}` } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: `Ecommerce Website Design in ${AREA}` } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: `Website Redesign in ${AREA}` } },
        ],
      },
    },
    {
      "@type": "Person",
      "@id": "https://freelanceramreshprajapatimumbai.in/#person",
      name: "Amresh Prajapati",
      jobTitle: "Freelance Website Designer & Developer",
      url: "https://freelanceramreshprajapatimumbai.in",
      worksFor: { "@type": "Organization", name: "Amresh Prajapati - Freelancer" },
      knowsAbout: ["Web Design", "UI/UX", "React", "JavaScript", "SEO", "WordPress"],
    },
    {
      "@type": "WebPage",
      "@id": `https://freelanceramreshprajapatimumbai.in/${SLUG}#webpage`,
      url: `https://freelanceramreshprajapatimumbai.in/${SLUG}`,
      name: `Website Designer in ${AREA} | Amresh Prajapati`,
      description,
      isPartOf: { "@id": "https://freelanceramreshprajapatimumbai.in/#website" },
      about: { "@id": `https://freelanceramreshprajapatimumbai.in/${SLUG}#business` },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://freelanceramreshprajapatimumbai.in" },
          { "@type": "ListItem", position: 2, name: AREA, item: `https://freelanceramreshprajapatimumbai.in/${SLUG}` },
        ],
      },
    },
    {
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

/* ─── Main component ─────────────────────────────────────────── */
export default function WebsiteDesignerVirarWest() {
  return (
    <>
      <Helmet>
        <title>{`Website Designer in ${AREA} | Amresh Prajapati`}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={`https://freelanceramreshprajapatimumbai.in/${SLUG}`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`Website Designer in ${AREA} | Amresh Prajapati`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`https://freelanceramreshprajapatimumbai.in/${SLUG}`} />
        <meta property="og:image" content="https://freelanceramreshprajapatimumbai.in/og-image.jpg" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="Amresh Prajapati" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Website Designer in ${AREA} | Amresh Prajapati`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://freelanceramreshprajapatimumbai.in/og-image.jpg" />

        {/* Extra SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Amresh Prajapati" />
        <meta name="geo.region" content="IN-MH" />
        <meta name="geo.placename" content={AREA} />

        <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
      </Helmet>

      <main className="wdv-root">
        {/* ── HERO ──────────────────────────────────────────────── */}
        <section className="wdv-hero">
          <div className="wdv-hero-noise" />
          <div className="wdv-hero-orb wdv-hero-orb-1" />
          <div className="wdv-hero-orb wdv-hero-orb-2" />
          <div className="wdv-hero-orb wdv-hero-orb-3" />

          <div className="wdv-grid-lines" aria-hidden="true">
            {[...Array(5)].map((_, i) => (
              <span key={i} className="wdv-vline" />
            ))}
          </div>

          <div className="wdv-wrap wdv-hero-inner">
            <div className="wdv-hero-left">
              <div className="wdv-eyebrow">
                <FaMapMarkerAlt />
                <span>Serving {AREA} &amp; Nearby Areas</span>
              </div>

              <h1 className="wdv-hero-h1">
                Website Designer in <em className="wdv-accent-text">{AREA}</em>
              </h1>

              <p className="wdv-hero-desc">
                I'm Amresh Prajapati — a freelance website designer who creates
                clean, modern and fast-loading websites for shops, clinics,
                coaching classes and startups in {AREA}. Design that looks
                sharp and actually brings you enquiries.
              </p>

              <div className="wdv-hero-ctas">
                <a
                  href={waLink(`Hello Amresh, I need a website designer in ${AREA}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wdv-btn wdv-btn-primary"
                >
                  <FaWhatsapp /> WhatsApp Now
                </a>
                <Link to="/contact" className="wdv-btn wdv-btn-ghost">
                  Get Free Quote <FaArrowRight />
                </Link>
              </div>

              <div className="wdv-trust-row">
                <div className="wdv-stars">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
                <span>Trusted by 50+ businesses across the Western Line</span>
              </div>
            </div>

            <div className="wdv-hero-right">
              <div className="wdv-hero-card">
                <div className="wdv-hero-card-header">
                  <span className="wdv-online-dot" />
                  <span>Available for new projects</span>
                </div>

                <h2>
                  Website Design
                  <br />
                  Services in {AREA}
                </h2>

                <ul className="wdv-feature-list">
                  {[
                    "Custom Visual Design",
                    "Mobile-First Layouts",
                    "UI/UX & Wireframing",
                    "Ecommerce Store Design",
                    "Local SEO Foundation",
                    "WhatsApp CTA Integration",
                  ].map((f) => (
                    <li key={f}>
                      <FaCheckCircle className="wdv-check" />
                      {f}
                    </li>
                  ))}
                </ul>

                <a
                  href={waLink(`Hello Amresh, I am from ${AREA} and need a website designed.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wdv-btn wdv-btn-primary wdv-btn-full"
                >
                  <FaPhone /> Call / WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── STATS ─────────────────────────────────────────────── */}
        <section className="wdv-stats-section">
          <div className="wdv-wrap">
            <StatsSection />
          </div>
        </section>

        {/* ── SERVICES ──────────────────────────────────────────── */}
        <section className="wdv-services">
          <div className="wdv-wrap">
            <div className="wdv-section-label">What I Design</div>
            <div className="wdv-services-header">
              <h2>
                Website Design Services
                <br />
                <span>Built for {AREA} Businesses</span>
              </h2>
              <p>
                Every design decision — colour, layout, spacing, copy — is
                made to help your business get more calls, enquiries and
                customers, not just to look good.
              </p>
            </div>

            <div className="wdv-services-grid">
              {services.map((s, i) => (
                <div className="wdv-service-card" key={s.title} style={{ "--delay": `${i * 0.07}s` }}>
                  <div className="wdv-service-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                  <div className="wdv-service-line" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY SECTION ───────────────────────────────────────── */}
        <section className="wdv-why">
          <div className="wdv-why-bg" />
          <div className="wdv-wrap wdv-why-inner">
            <div className="wdv-why-content">
              <div className="wdv-section-label">Local Design Expert</div>
              <h2>
                Why {AREA} Businesses
                <br />
                Need Good Website Design
              </h2>
              <p>
                Design is the first impression your business makes online.
                Before a customer in {AREA} ever calls you, they see your
                website — and a cluttered, outdated or slow site quietly
                sends them to a competitor instead.
              </p>
              <p>
                Good website design is not decoration — it's the layout,
                colour and structure that guide a visitor's eye straight to
                what you offer and how to reach you. For shops, clinics,
                coaching classes and service businesses in {AREA}, that
                clarity is what turns a passing visitor into an enquiry.
              </p>
              <p>
                I design with a mobile-first approach, clear call-to-action
                placement, WhatsApp contact buttons and local SEO structure
                built in from the first wireframe — so the design and the
                ranking work together, not against each other.
              </p>

              <div className="wdv-why-tags">
                {[
                  "Shops",
                  "Clinics",
                  "Coaching Classes",
                  "Startups",
                  "Real Estate",
                  "Consultants",
                  "Service Providers",
                  "Freelancers",
                ].map((t) => (
                  <span key={t} className="wdv-tag">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="wdv-why-aside">
              <div className="wdv-aside-card">
                <h3>Best For</h3>
                <ul>
                  {[
                    "Local Businesses in " + AREA,
                    "Startups & Agencies",
                    "Coaching Classes",
                    "Clinics & Consultants",
                    "Shops & Service Providers",
                    "Portfolio Websites",
                    "Landing Pages",
                    "Ecommerce Websites",
                  ].map((i) => (
                    <li key={i}>
                      <span className="wdv-dot" />
                      {i}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="wdv-aside-cta">
                <p>Ready to start?</p>
                <a
                  href={waLink(`Hello Amresh, I am from ${AREA} and want my website designed.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wdv-btn wdv-btn-primary wdv-btn-full"
                >
                  <FaWhatsapp /> Start on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── PROCESS ───────────────────────────────────────────── */}
        <section className="wdv-process">
          <div className="wdv-wrap">
            <div className="wdv-section-label center">Simple Process</div>
            <h2 className="wdv-process-title">
              From First Chat to
              <br />
              <span>Live Design — 3 Steps</span>
            </h2>

            <div className="wdv-process-track">
              <div className="wdv-process-line" />
              {steps.map((s) => (
                <div className="wdv-process-step" key={s.n}>
                  <div className="wdv-process-num">{s.n}</div>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SEO CONTENT ───────────────────────────────────────── */}
        <section className="wdv-seo-section">
          <div className="wdv-wrap wdv-seo-inner">
            <div className="wdv-seo-aside">
              <div className="wdv-aside-card accent">
                <h3>What You Get</h3>
                <ul>
                  {[
                    "Custom responsive design",
                    "UI/UX wireframes",
                    "SEO title & meta setup",
                    "Schema markup (JSON-LD)",
                    "WhatsApp CTA integration",
                    "Fast loading structure",
                    "Clean modern UI",
                    "Sitemap & robots.txt",
                    "Google Search Console setup",
                  ].map((i) => (
                    <li key={i}>
                      <span className="wdv-dot accent" />
                      {i}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="wdv-seo-content">
              <div className="wdv-section-label">SEO-Ready Design</div>
              <h2>
                Design That Looks Premium
                <br />
                <span>and Actually Ranks</span>
              </h2>
              <p>
                A website's design and its SEO are not separate jobs. The way
                a page is structured — headings, image placement, loading
                order — directly affects how Google reads it. Every design I
                build guides a visitor naturally from the hero section
                through services, trust signals and finally the contact
                button.
              </p>
              <p>
                For local search, this page is built around{" "}
                <strong>{PRIMARY_KEYWORD}</strong>, along with related terms
                like affordable website designer in {AREA}, best website
                designer near {AREA} and website design services in {AREA}.
                Structured correctly and added to the sitemap, pages like
                this drive long-term organic traffic from local searches.
              </p>
              <p>
                Every design ships with proper schema markup (LocalBusiness,
                FAQPage, BreadcrumbList), Open Graph tags for sharing,
                sitemap.xml, robots.txt and canonical URL setup — the
                technical layer that most "design only" freelancers skip.
              </p>
              <p>
                If you're searching for a {PRIMARY_KEYWORD} who handles
                design, development and SEO under one roof, I'm ready to
                help. Let's design a website that brings real enquiries from{" "}
                {AREA} and nearby areas like {NEARBY.join(", ")}.
              </p>
            </div>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────── */}
        <section className="wdv-faq">
          <div className="wdv-wrap">
            <div className="wdv-section-label center">FAQ</div>
            <h2 className="wdv-faq-title">
              Common Questions from
              <br />
              <span>{AREA} Clients</span>
            </h2>

            <div className="wdv-faq-grid">
              {faqs.map(({ q, a }) => (
                <div className="wdv-faq-item" key={q}>
                  <h3>{q}</h3>
                  <p>{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── NEARBY AREAS ──────────────────────────────────────── */}
        <section className="wdv-nearby">
          <div className="wdv-wrap">
            <p className="wdv-nearby-label">Also serving nearby areas</p>
            <div className="wdv-nearby-chips">
              {NEARBY.map((n) => (
                <span key={n} className="wdv-chip">
                  {n}
                </span>
              ))}
              <span className="wdv-chip">Mumbai</span>
            </div>
          </div>
        </section>

        {/* ── CTA BANNER ────────────────────────────────────────── */}
        <section className="wdv-cta-banner">
          <div className="wdv-cta-orb-1" />
          <div className="wdv-cta-orb-2" />
          <div className="wdv-wrap wdv-cta-inner">
            <div>
              <h2>Need a Website Designer in {AREA}?</h2>
              <p>Let's design something fast, beautiful and built to rank.</p>
            </div>
            <div className="wdv-cta-btns">
              <a
                href={waLink(`Hello Amresh, I am from ${AREA} and need a website designer.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="wdv-btn wdv-btn-primary"
              >
                <FaWhatsapp /> WhatsApp Now
              </a>
              <Link to="/contact" className="wdv-btn wdv-btn-ghost">
                Request a Quote <FaArrowRight />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
